import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface NewsletterSignupProps {
  variant?: "header" | "inline" | "card";
  title?: string;
  description?: string;
}

const NewsletterSignup = ({ 
  variant = "card", 
  title = "Get Trading Insights", 
  description = "Join thousands of traders receiving our weekly newsletter with market analysis, trading strategies, and exclusive funding opportunities" 
}: NewsletterSignupProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('📧 Attempting newsletter signup:', { email: email.toLowerCase().trim(), variant });
      
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .insert([
          {
            email: email.toLowerCase().trim(),
            source: `website_${variant}`,
            is_active: true
          }
        ])
        .select();

      console.log('📧 Newsletter signup result:', { data, error });

      if (error) {
        console.error('📧 Newsletter signup error details:', error);
        
        // Handle duplicate email case
        if (error.code === '23505') {
          setIsSubscribed(true);
          toast({
            title: "Already Subscribed!",
            description: "This email is already receiving our newsletter.",
          });
          setEmail("");
          return;
        }
        
        throw error;
      }

      // Send the free Trading Secrets PDF email
      supabase.functions.invoke("send-pdf-welcome", {
        body: { email: email.toLowerCase().trim() },
      }).catch((err) => console.error("PDF email send failed:", err));
      
      setIsSubscribed(true);
      toast({
        title: "Check your email! 📧",
        description: "Your FREE 7 Trading Secrets PDF is on its way to your inbox.",
      });
      
      setEmail("");
      
    } catch (error) {
      console.error('Newsletter signup error:', error);
      toast({
        title: "Subscription Failed",
        description: "There was an error subscribing to the newsletter. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (variant === "header") {
    return (
      <div className="bg-trading-primary/10 border-l-4 border-trading-primary p-3 rounded-r-md">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-trading-primary shrink-0" />
          <Input
            type="email"
            placeholder="Enter email for trading tips"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="text-sm h-8 min-w-[200px]"
            disabled={isSubmitting || isSubscribed}
          />
          <Button 
            type="submit" 
            size="sm" 
            variant="trading"
            disabled={isSubmitting || isSubscribed}
          >
            {isSubmitting ? "..." : isSubscribed ? <CheckCircle className="h-4 w-4" /> : "Join"}
          </Button>
        </form>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className="bg-gradient-card border border-border rounded-lg p-6 my-8">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-card-foreground mb-2 flex items-center justify-center gap-2">
            <Mail className="h-5 w-5 text-trading-primary" />
            {title}
          </h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1"
            disabled={isSubmitting || isSubscribed}
          />
          <Button 
            type="submit" 
            variant="affiliate"
            disabled={isSubmitting || isSubscribed}
          >
            {isSubmitting ? "Subscribing..." : isSubscribed ? "Subscribed!" : "Subscribe"}
          </Button>
        </form>
        {isSubscribed && (
          <p className="text-sm text-trading-primary mt-2 text-center">
            ✓ You'll receive exclusive trading insights and funding opportunities!
          </p>
        )}
      </div>
    );
  }

  // Card variant (default)
  return (
    <Card className="bg-gradient-card border-border shadow-card">
      <CardHeader className="text-center">
        <div className="flex items-center justify-center mb-2">
          <Mail className="h-8 w-8 text-trading-primary" />
        </div>
        <CardTitle className="text-card-foreground">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting || isSubscribed}
            />
          </div>
          <Button 
            type="submit" 
            variant="affiliate"
            className="w-full"
            disabled={isSubmitting || isSubscribed}
          >
            {isSubmitting ? "Subscribing..." : isSubscribed ? "Successfully Subscribed!" : "Get Free Trading Insights"}
          </Button>
          {isSubscribed && (
            <p className="text-sm text-trading-primary text-center">
              ✓ You'll receive our next newsletter with exclusive insights and funding opportunities!
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default NewsletterSignup;